import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router'
import {
  AppBar,
  Box,
  Chip,
  Divider,
  ListItemIcon,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
} from '@mui/material'
import LockIcon from '@mui/icons-material/Lock'
import MenuIcon from '@mui/icons-material/Menu'
import PersonIcon from '@mui/icons-material/Person'
import LogoutIcon from '@mui/icons-material/Logout'
import MenuOpenIcon from '@mui/icons-material/MenuOpen'

import Dialog from './Dialog'
import DialogImpersonation from './DialogImpersonation'
import DialogCambioPassword from './DialogCambioPassword'
import { config } from '../settings/configs'
import { colours } from '../settings/settings'
import { mailTo } from '../hooks/utils-functions'
import { useAuth } from '../hooks/auth-hook'
import { useFeedback } from '../hooks/feedback-hook'
import {
  useLazyGetAffiliazioneByCodiceQuery,
  useGetImpostazioniContattiQuery,
  useChangePasswordMutation,
} from '../hooks/api-slice'
import { setIsOpenDrawer } from '../container/home'

const Header = () => {
  const infoUtente = useSelector(state => state.home.infoUtente)
  const { data: contatti } = useGetImpostazioniContattiQuery()
  const isOpenDrawer = useSelector(state => state.home.isOpenDrawer)
  const {
    auth,
    admin,
    societa,
    arbitro,
    designatore,
    omologatore,
    impersonation,
    codiceUtente,
  } = infoUtente
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [triggerGetAffiliazioneByCodice] = useLazyGetAffiliazioneByCodiceQuery()
  const [changePasswordMutation] = useChangePasswordMutation()

  const handleClickLogin = () => navigate('/')

  const { login, logout } = useAuth()
  const { setFeedback } = useFeedback()

  const toggleMainMenu = () => dispatch(setIsOpenDrawer(!isOpenDrawer))

  const [anchorEl, setAnchorEl] = useState(null)
  const openUserMenu = Boolean(anchorEl)
  const handleClickOpenUserMenu = event => setAnchorEl(event.currentTarget)
  const handleCloseUserMenu = () => setAnchorEl(null)

  const [infoSocieta, setInfoSocieta] = useState()
  const [openInfoSocieta, setOpenInfoSocieta] = useState()
  const handleClickOpenInfoSocieta = async () => getInfoSocieta()
  const handleCloseInfoSocieta = () => {
    setOpenInfoSocieta()
    setInfoSocieta()
  }

  const [openImpersonation, setOpenImpersonation] = useState()
  const handleClickOpenImpersonation = async () => setOpenImpersonation(true)
  const handleCloseImpersonation = () => setOpenImpersonation()

  const [openCambioPassword, setOpenCambioPassword] = useState()
  const handleClickOpenCambioPassword = () => setOpenCambioPassword(true)
  const handleCloseCambioPassword = () => setOpenCambioPassword()

  const getInfoSocieta = async () => {
    const result = await triggerGetAffiliazioneByCodice(codiceUtente)
    if (!result?.error) {
      setInfoSocieta(result?.data)
      setOpenInfoSocieta(true)
    }
  }

  const changePassword = async (oldPassword, newPassword) => {
    const body = { oldPassword, newPassword }
    const result = await changePasswordMutation(body)
    if (!result?.error) {
      setFeedback('Password aggiornata!', false)
      handleCloseCambioPassword()
      login({ codice: codiceUtente, password: newPassword })
    }
  }

  const triggerImpersonation = impersonateUser => {
    if (!impersonateUser) {
      setFeedback('Selezionare un utente!')
      return
    }

    login({ impersonateUser, triggerFunction: handleCloseImpersonation })
  }

  return (
    <AppBar
      position='fixed'
      open={isOpenDrawer}
      sx={{
        color: colours.blueDark,
        backgroundColor: impersonation ? colours.highlight : colours.headerBack,
        zIndex: theme => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar>
        <Box sx={{ display: { xs: 'flex' }, flexGrow: 1 }}>
          <IconButton
            style={{ color: colours.blueDark }}
            onClick={toggleMainMenu}
          >
            {isOpenDrawer ? (
              <MenuOpenIcon fontSize='large' />
            ) : (
              <MenuIcon fontSize='large' />
            )}
          </IconButton>
        </Box>
        <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexGrow: 5 }}>
          <center>
            <a
              target='_blank'
              rel='noreferrer noopener'
              href={config?.website}
              style={{ textDecoration: 'none', color: colours.blueDark }}
            >
              <h2 style={{ margin: 0 }}>{config?.name}</h2>
            </a>
          </center>
        </Box>
        <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 10 }}>
          <center>
            <h3 style={{ margin: 0 }}>
              Piattaforma per i campionati di pallavolo
            </h3>
          </center>
        </Box>
        <Box sx={{ display: { xs: 'flex' }, flexGrow: 0 }}>
          <center>
            {(auth && (
              <>
                <Chip
                  variant='outlined'
                  style={{
                    fontWeight: 'bold',
                    color: colours.headerBack,
                    borderColor: colours.headerBack,
                    backgroundColor: colours.blueDark,
                  }}
                  label={
                    (impersonation && `Impersonazione ${codiceUtente}`) ||
                    (admin && 'Amministratore') ||
                    (arbitro && codiceUtente) ||
                    (designatore && 'Designatore') ||
                    (omologatore && 'Omologatore') ||
                    (societa && `Società ${codiceUtente}`) ||
                    `Utente ${codiceUtente}`
                  }
                  onClick={handleClickOpenUserMenu}
                />
                <Menu
                  open={openUserMenu}
                  anchorEl={anchorEl}
                  onClick={handleCloseUserMenu}
                  onClose={handleCloseUserMenu}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  slotProps={{
                    paper: {
                      sx: {
                        marginTop: 1.5,
                        overflow: 'visible',
                        filter: `drop-shadow(0px 2px 8px ${colours.backDrop})`,
                        '&:before': {
                          content: '""',
                          display: 'block',
                          position: 'absolute',
                          top: 0,
                          right: 14,
                          width: 10,
                          height: 10,
                          bgcolor: 'background.paper',
                          transform: 'translateY(-50%) rotate(45deg)',
                          zIndex: 0,
                        },
                      },
                    },
                  }}
                >
                  {societa && (
                    <MenuItem onClick={handleClickOpenInfoSocieta}>
                      <ListItemIcon>
                        <PersonIcon fontSize='small' />
                      </ListItemIcon>
                      Info società
                    </MenuItem>
                  )}
                  {(societa || arbitro) && (
                    <MenuItem onClick={handleClickOpenCambioPassword}>
                      <ListItemIcon>
                        <LockIcon fontSize='small' />
                      </ListItemIcon>
                      Cambio password
                    </MenuItem>
                  )}
                  {(societa || arbitro) && <Divider />}
                  {admin && (
                    <MenuItem onClick={handleClickOpenImpersonation}>
                      <ListItemIcon>
                        <PersonIcon fontSize='small' />
                      </ListItemIcon>
                      Impersona utente
                    </MenuItem>
                  )}
                  <MenuItem onClick={() => logout(impersonation)}>
                    <ListItemIcon>
                      <LogoutIcon fontSize='small' />
                    </ListItemIcon>
                    {impersonation ? 'Fine impersonazione' : 'Logout'}
                  </MenuItem>
                </Menu>
              </>
            )) || (
              <Chip
                variant='outlined'
                style={{
                  fontWeight: 'bold',
                  color: colours.headerBack,
                  borderColor: colours.headerBack,
                  backgroundColor: colours.blueDark,
                }}
                label='Login'
                onClick={handleClickLogin}
              />
            )}
          </center>
        </Box>
      </Toolbar>
      {societa && (
        <Dialog
          open={openInfoSocieta}
          handleClose={handleCloseInfoSocieta}
          title='Informazioni societarie'
          textContent={
            <span>
              Codice società: <b>{infoSocieta?.codiceSocieta || '??'}</b>
              <br />
              Ragione sociale:{' '}
              <b>{infoSocieta?.denominazioneSocieta || '??'}</b>
              <br />
              <br />
              Referente: <b>{infoSocieta?.referente || '??'}</b>
              <br />
              Ruolo: <b>{infoSocieta?.ruolo || '??'}</b>
              <br />
              Telefono: <b>{infoSocieta?.telefono || '??'}</b>
              <br />
              Mail: <b>{infoSocieta?.mail || '??'}</b>
              <br />
              <br />
              Si prega di segnalare eventuali dati mancanti o errati a{' '}
              {mailTo(contatti?.MailSegreteria)}.
            </span>
          }
        />
      )}
      {(societa || arbitro) && (
        <DialogCambioPassword
          open={openCambioPassword}
          handleClose={handleCloseCambioPassword}
          triggerFunction={changePassword}
        />
      )}
      {admin && (
        <DialogImpersonation
          open={openImpersonation}
          handleClose={handleCloseImpersonation}
          triggerFunction={triggerImpersonation}
        />
      )}
    </AppBar>
  )
}

export default Header
